// pages/admin/OrderList.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  getAllOrdersAPI,
  getOrderByIdAPI,
  updateOrderStatusAPI,
  deleteOrderAPI,
} from "../../../../api/adminOrderAPIs.ts";
import {
  BlockBetween,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PreviewCard,
  ReactDataTable,
} from "../../../../components/Component";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const OrderList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await getAllOrdersAPI();
        setOrders(response.data.data.filter((order) => order.orderStatus !== "Pending"));
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        toast.error("Failed to fetch orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const toggleStatusDropdown = (orderId) => {
    setStatusDropdownOpen((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatusAPI({
        id: orderId,
        body: { orderStatus: newStatus },
      });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, orderStatus: newStatus } : order
        )
      );
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder((prev) => ({ ...prev, orderStatus: newStatus }));
      }
      toast.success(`Order ${orderId} status updated to ${newStatus}`);
    } catch (error) {
      console.error("Failed to update order status:", error);
      toast.error("Failed to update order status");
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      await deleteOrderAPI({ id: orderToDelete._id });
      setOrders((prevOrders) => prevOrders.filter((order) => order._id !== orderToDelete._id));
      toast.success(`Order ${orderToDelete._id} deleted successfully`);
      setDeleteModalOpen(false);
      setOrderToDelete(null);
    } catch (error) {
      console.error("Failed to delete order:", error);
      toast.error(error.response?.data?.message || "Failed to delete order");
    }
  };

  const openDeleteModal = (order) => {
    setOrderToDelete(order);
    setDeleteModalOpen(true);
  };

  const toggleModal = () => setModalOpen((prev) => !prev);

  const handleViewOrder = async (orderId) => {
    try {
      const response = await getOrderByIdAPI({ id: orderId });
      setSelectedOrder(response.data.data);
      toggleModal();
    } catch (error) {
      console.error("Failed to fetch order details:", error);
      toast.error("Failed to fetch order details");
    }
  };

  const statusOptions = [
    "Pending",
    "Confirmed",
    "Shipped",
    "Delivered",
    "Cancelled",
    "Return Requested",
  ];

  const columns = [
    {
      name: "Order ID",
      selector: (row) => row._id,
      cell: (row) => (
        <span
          className="cursor-pointer text-primary fw-bold"
          onClick={() => navigate(`/admin/orders/${row._id}`)}
        >
          {row._id}
        </span>
      ),
    },
    {
      name: "User",
      selector: (row) => `${row.user_id.first_name} ${row.user_id.last_name}`,
      cell: (row) => <span className="text-muted">{`${row.user_id.first_name} ${row.user_id.last_name}`}</span>,
      sortable: true,
    },
    {
      name: "Products",
      selector: (row) => row.products[0]?.productId.name || "N/A",
      cell: (row) => (
        <span>{row.products.map((p) => p.productId.name).join(", ") || "N/A"}</span>
      ),
    },
    {
      name: "Total Amount",
      selector: (row) => row.totalAmount,
      cell: (row) => <span className="text-dark fw-bold">₹{row.totalAmount.toFixed(2)}</span>,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.orderStatus,
      cell: (row) => (
        <Dropdown
          isOpen={statusDropdownOpen[row._id] || false}
          toggle={() => toggleStatusDropdown(row._id)}
        >
          <DropdownToggle
            caret
            color={
              row.orderStatus === "Delivered"
                ? "success"
                : row.orderStatus === "Cancelled"
                  ? "danger"
                  : "warning"
            }
            className="btn-sm"
          >
            {row.orderStatus}
          </DropdownToggle>
          <DropdownMenu>
            {statusOptions.map((status) => (
              <DropdownItem
                key={status}
                onClick={() => handleStatusUpdate(row._id, status)}
                disabled={row.orderStatus === status}
              >
                {status}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      ),
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => row.orderDate,
      cell: (row) => <span>{new Date(row.orderDate).toLocaleDateString()}</span>,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            title="View Order"
            onClick={() => navigate(`/admin/orders/${row._id}`)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              padding: "5px 12px",
              fontSize: "13px",
              fontWeight: 500,
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: "#0dcaf0",
              color: "#fff",
              whiteSpace: "nowrap",
              lineHeight: 1,
            }}
          >
            <Icon name="eye" style={{ fontSize: "14px", lineHeight: 1 }} />
            <span>View</span>
          </button>
          <button
            title="Delete Order"
            onClick={() => openDeleteModal(row)}
            disabled={row.orderStatus === "Shipped" || row.orderStatus === "Delivered"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              padding: "5px 12px",
              fontSize: "13px",
              fontWeight: 500,
              border: "none",
              borderRadius: "6px",
              cursor: row.orderStatus === "Shipped" || row.orderStatus === "Delivered" ? "not-allowed" : "pointer",
              backgroundColor: row.orderStatus === "Shipped" || row.orderStatus === "Delivered" ? "#adb5bd" : "#dc3545",
              color: "#fff",
              whiteSpace: "nowrap",
              lineHeight: 1,
              opacity: row.orderStatus === "Shipped" || row.orderStatus === "Delivered" ? 0.65 : 1,
            }}
          >
            <Icon name="trash" style={{ fontSize: "14px", lineHeight: 1 }} />
            <span>Delete</span>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Head title="Order List" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Order List
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have a total of {orders.length} orders.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={() => navigate("/admin/orders/new")}
                className="btn-icon"
              >
                <Icon name="plus" /> Add Order
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <BlockContent>
          <PreviewCard>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                <div className="text-center">
                  <div
                    className="spinner-grow text-primary"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading orders, please wait...</p>
                </div>
              </div>
            ) : (
              <ReactDataTable
                data={orders}
                columns={columns}
                pagination
                actions
                expandableRows
              />
            )}
          </PreviewCard>
        </BlockContent>

        {/* View Order Modal */}
        <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
          <ModalHeader toggle={toggleModal} className="bg-primary text-white">
            Order Details - {selectedOrder?._id}
          </ModalHeader>
          <ModalBody>
            {selectedOrder ? (
              <div>
                <div className="mb-3">
                  <h6 className="text-muted">Customer</h6>
                  <p className="fw-bold">{`${selectedOrder.user_id.first_name} ${selectedOrder.user_id.last_name}`}</p>
                  <p className="text-muted">{selectedOrder.user_id.email}</p>
                </div>
                <div className="mb-3">
                  <h6 className="text-muted">Order Date</h6>
                  <p>{new Date(selectedOrder.orderDate).toLocaleString()}</p>
                </div>
                <div className="mb-3">
                  <h6 className="text-muted">Total Amount</h6>
                  <p className="fw-bold text-dark">₹{selectedOrder.totalAmount.toFixed(2)}</p>
                </div>
                <div className="mb-3">
                  <h6 className="text-muted">Status</h6>
                  <span className={`badge ${selectedOrder.orderStatus === "Delivered" ? "bg-success" : "bg-warning"} text-white`}>
                    {selectedOrder.orderStatus}
                  </span>
                </div>
                <div className="mb-3">
                  <h6 className="text-muted">Products</h6>
                  <ul className="list-group">
                    {selectedOrder.products.map((product) => (
                      <li key={product.productId._id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{product.productId.name}</span>
                        <span>Qty: {product.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-3">
                  <h6 className="text-muted">Shipping Address</h6>
                  <p>
                    {selectedOrder.shippingAddressId
                      ? `${selectedOrder.shippingAddressId.addressLine1}, ${selectedOrder.shippingAddressId.city}, ${selectedOrder.shippingAddressId.state} ${selectedOrder.shippingAddressId.postalCode}`
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <h6 className="text-muted">Payment</h6>
                  <p>
                    {selectedOrder.payment_id
                      ? `${selectedOrder.payment_id.paymentMethod} - ${selectedOrder.payment_id.status}`
                      : "N/A"}
                  </p>
                </div>
              </div>
            ) : (
              <p>Loading order details...</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Close
            </Button>
            <Button color="primary" onClick={() => navigate(`/admin/orders/${selectedOrder?._id}`)}>
              Full Details
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
          <ModalHeader toggle={() => setDeleteModalOpen(false)} className="bg-danger text-white">
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            <p>Are you sure you want to delete the following order?</p>
            <div className="bg-light p-3 rounded">
              <p className="mb-1"><strong>Order ID:</strong> {orderToDelete?._id}</p>
              <p className="mb-1"><strong>Customer:</strong> {orderToDelete?.user_id?.first_name} {orderToDelete?.user_id?.last_name}</p>
              <p className="mb-0"><strong>Total Amount:</strong> ₹{orderToDelete?.totalAmount?.toFixed(2)}</p>
            </div>
            <p className="text-danger mt-3 small">⚠️ This action cannot be undone. This will permanently delete the order and its associated payment and shipping records.</p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteOrder}>
              <Icon name="trash" /> Delete Order
            </Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default OrderList;